import { PageMeta } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { ImageSlot } from "@/components/ImageSlot";
import { PageHero, SectionHeader } from "@/components/Section";
import { Ambient } from "@/components/Ambient";
import { SEO, MAHAVIDYA_INTRO, MAHAVIDYAS, ADI_MAA, SHYAMA_MAA } from "@/content/siteContent";

const VidyaEntry = ({ vidya, flip }) => (
    <Reveal>
        <article
            className={`grid items-center gap-8 lg:grid-cols-2 lg:gap-16`}
            data-testid={`vidya-entry-${vidya.num}`}
        >
            <div className={flip ? "lg:order-2" : ""}>
                <ImageSlot slotKey={vidya.slot} aspect="4 / 3" />
            </div>
            <div className={flip ? "lg:order-1" : ""}>
                <span className="font-display text-5xl font-medium text-[#C8A15A]/50">
                    {String(vidya.num).padStart(2, "0")}
                </span>
                <h3 className="font-display mt-4 text-3xl font-semibold leading-tight text-[#FFF5E6] sm:text-4xl">
                    {vidya.name}
                </h3>
                <p className="mt-2 text-sm font-light uppercase tracking-[0.2em] text-[#C8A15A]">
                    {vidya.tagline}
                </p>
                <p className="body-copy mt-6 max-w-xl text-[#FFF5E6]/75">{vidya.body}</p>
            </div>
        </article>
    </Reveal>
);

export default function Mahavidyas() {
    return (
        <>
            <PageMeta title={SEO.mahavidyas.title} description={SEO.mahavidyas.description} />
            <PageHero
                eyebrow="The Ten Mahavidyas"
                title="The Ten Mahavidyas and Adi Maa"
                intro="Meet the ten forms of the Divine Mother honoured in our Puja and learn about Adi Maa's place at the centre of our celebration."
            />

            <section className="surface-dark py-20 sm:py-24" data-testid="vidya-intro">
                <div className="mx-auto max-w-4xl px-5 sm:px-8">
                    <SectionHeader eyebrow="An Introduction" title={MAHAVIDYA_INTRO.title} dark />
                    <Reveal delay={0.1}>
                        <div className="body-copy mt-8 space-y-5 text-[#FFF5E6]/75">
                            {MAHAVIDYA_INTRO.body.map((p, i) => (
                                <p key={i}>{p}</p>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            <section
                className="surface-dark relative space-y-24 overflow-hidden border-t border-[#C8A15A]/10 py-20 sm:py-28"
                data-testid="vidya-list"
                aria-label="The ten Mahavidyas"
            >
                <Ambient />
                <div className="relative mx-auto max-w-7xl space-y-24 px-5 sm:px-8">
                    {MAHAVIDYAS.map((v, i) => (
                        <VidyaEntry key={v.num} vidya={v} flip={i % 2 === 1} />
                    ))}
                </div>
            </section>

            {/* Adi Maa — central to our arrangement */}
            <section className="surface-maroon relative overflow-hidden py-20 sm:py-28" data-testid="adi-maa-section">
                <Ambient />
                <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:gap-16">
                    <Reveal>
                        <ImageSlot slotKey="adiMaa" aspect="4 / 3" />
                    </Reveal>
                    <div>
                        <SectionHeader eyebrow="At the Centre" title={ADI_MAA.title} dark />
                        <Reveal delay={0.1}>
                            <p className="body-copy mt-7 max-w-xl text-[#FFF5E6]/75">{ADI_MAA.body}</p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Shyama Maa — the wider celebration */}
            <section className="surface-ivory py-20 sm:py-28" data-testid="shyama-maa-section">
                <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:gap-16">
                    <div className="order-2 lg:order-1">
                        <SectionHeader eyebrow="The Wider Celebration" title={SHYAMA_MAA.title} />
                        <Reveal delay={0.1}>
                            <p className="body-copy mt-7 max-w-xl">{SHYAMA_MAA.body}</p>
                        </Reveal>
                    </div>
                    <Reveal className="order-1 lg:order-2">
                        <ImageSlot slotKey="shyamaMaa" aspect="4 / 3" light />
                    </Reveal>
                </div>
            </section>
        </>
    );
}
